import { type AdminClient } from '@apx/admin-sdk';
import { fmtDateTime, fmtTime } from '../format.js';
import { io, type Socket } from 'socket.io-client';
import type { RoundEventFrame } from '@apx/protocol';

export interface InspectorPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  gatewayUrl: string;
  roundEngineBaseUrl: string;
}

// Frame shape comes from @apx/protocol — see RoundEventFrame.
type RoundEvent = RoundEventFrame;

const MAX_LOG_ROWS = 50;

/** Convergence-poll cadence for the per-slot totals. Two indexed queries per
 *  tick, gated on tab visibility. */
const TOTALS_POLL_MS = 3_000;

/** Display label per gameId; falls back to the raw id for unknown games. */
const GAME_LABELS: Record<string, string> = {
  greedyking: 'Greedyking',
  sicbo: 'Sic Bo',
  dragontiger: 'Dragon & Tiger',
  singaporelottery: 'Singapore Lottery',
  baccarat: 'Baccarat',
};

export async function renderInspectorPage(
  root: HTMLElement,
  deps: InspectorPageDeps,
): Promise<void> {
  const gameLabel = GAME_LABELS[deps.gameId] ?? deps.gameId;
  root.innerHTML = `
    <h1 class="page-title">Live Round Inspector — ${gameLabel}</h1>
    <p class="page-sub">Streams round events from the gateway and renders per-slot totals for the active round. Read-only inspector token, scoped to this integrator.</p>

    <div class="card">
      <h2>Connection</h2>
      <div class="kv">
        <dt>Status</dt><dd><span class="pill" id="ws-status">connecting…</span></dd>
        <dt>Active round</dt><dd id="active-round">—</dd>
        <dt>Gateway</dt><dd><code>${deps.gatewayUrl}</code></dd>
        <dt>Room</dt><dd><code>game:${deps.gameId}:integrator:${deps.integratorId}</code></dd>
      </div>
    </div>

    <div class="card">
      <h2>Per-slot totals (current round)</h2>
      <div id="totals-heatmap"><p class="muted">No active round.</p></div>
    </div>

    <div class="card">
      <div class="row" style="justify-content:space-between;margin-top:0">
        <h2 style="margin:0">Event stream (last ${MAX_LOG_ROWS})</h2>
        <div class="row" style="margin:0;gap:8px">
          <input id="evt-filter" type="text" placeholder="filter by type (substring)" style="background:var(--panel-2);color:var(--text);border:1px solid var(--border);border-radius:var(--radius);padding:5px 8px;font-size:12px;width:200px" />
          <button id="evt-pause" class="ghost">Pause</button>
          <button id="evt-clear" class="ghost">Clear</button>
        </div>
      </div>
      <div id="event-log" style="margin-top:10px"><p class="muted">Waiting for events…</p></div>
    </div>
  `;

  const statusEl = root.querySelector<HTMLElement>('#ws-status')!;
  const activeRoundEl = root.querySelector<HTMLElement>('#active-round')!;
  const heatmapEl = root.querySelector<HTMLElement>('#totals-heatmap')!;
  const logEl = root.querySelector<HTMLElement>('#event-log')!;

  let activeRoundId: string | null = null;
  let socket: Socket | null = null;
  const events: RoundEvent[] = [];
  let paused = false;
  let typeFilter = '';
  // Monotonic ticket for refreshTotals(). Overlapping calls resolve in network
  // order, so only the newest one is allowed to paint.
  let totalsSeq = 0;
  /** True while a totals fetch is outstanding, so the poll skips its tick rather
   *  than stacking requests on a slow backend. Event-driven refreshes are never
   *  skipped. */
  let totalsInFlight = false;
  /** Consecutive background-refresh failures. One blip must not wipe a good
   *  render, but a sustained outage must not hide behind stale numbers either. */
  let totalsFailStreak = 0;
  /** Bumped on every round.opened. refreshActive() captures it and skips its
   *  activeRoundId write if it changed, so a slow REST read cannot roll the page
   *  back to the previous round. */
  let roundGen = 0;

  function setStatus(label: string, kind: 'live' | 'warn' | ''): void {
    statusEl.textContent = label;
    statusEl.className = kind ? `pill ${kind}` : 'pill';
  }

  /** `background` marks an automatic refresh (poll, WS event, reconnect) rather
   *  than the mount fetch. Background failures tolerate a couple of blips before
   *  replacing a good render with an error. */
  async function refreshTotals(opts: { background?: boolean } = {}): Promise<void> {
    // Capture before the await: round.opened can move activeRoundId mid-flight,
    // and the render must stay bound to the round this fetch asked for.
    const roundId = activeRoundId;
    const seq = ++totalsSeq;
    if (!roundId) {
      heatmapEl.innerHTML = '<p class="muted">No active round.</p>';
      return;
    }
    totalsInFlight = true;
    try {
      const { totals } = await deps.client.roundTotals(deps.roundEngineBaseUrl, roundId);
      if (seq !== totalsSeq) return;
      totalsFailStreak = 0;
      const slots = Object.keys(totals);
      if (slots.length === 0) {
        heatmapEl.innerHTML = '<p class="muted">No bets yet for this round.</p>';
        return;
      }
      const maxAmt = Math.max(...slots.map((s) => Number(totals[s] ?? 0)));
      heatmapEl.innerHTML = `<table>
        <thead><tr><th>Slot</th><th>Total</th><th>Share</th></tr></thead>
        <tbody>${slots
          .map((s) => {
            const amt = Number(totals[s] ?? 0);
            const pct = maxAmt > 0 ? Math.round((amt / maxAmt) * 100) : 0;
            return `<tr>
              <td><code>${s}</code></td>
              <td>${amt.toLocaleString()}</td>
              <td>
                <div style="background:linear-gradient(90deg,var(--accent) ${pct}%, var(--panel-2) ${pct}%);height:14px;border-radius:3px;width:160px"></div>
              </td>
            </tr>`;
          })
          .join('')}</tbody>
      </table>`;
    } catch (err) {
      if (seq !== totalsSeq) return;
      totalsFailStreak += 1;
      // One dropped request should not blank a correct table — the next tick
      // usually recovers. Surface it once it is clearly not transient.
      if (opts.background && totalsFailStreak < 3) return;
      heatmapEl.innerHTML = `<p class="error">Failed to fetch totals — ${escapeHtml(
        describeFetchError(err),
      )}</p>`;
    } finally {
      totalsInFlight = false;
    }
  }

  /** Forwards `background` to refreshTotals so a reconnect resync gets the same
   *  blip tolerance as the poll. Only the mount call is foreground. */
  async function refreshActive(opts: { background?: boolean } = {}): Promise<void> {
    // Everything here is guarded: a misconfigured edge (no /round-engine/*
    // proxy on the admin vhost) answers with the SPA's own index.html at
    // status 200, so res.json() throws SyntaxError. Left unhandled that
    // rejected out of renderInspectorPage as an "Uncaught (in promise)"
    // console error and aborted the rest of the page setup silently. Surface
    // it in the card instead.
    const gen = roundGen;
    try {
      const res = await fetch(
        `${deps.roundEngineBaseUrl}/v1/rounds/active?integratorId=${deps.integratorId}&gameId=${deps.gameId}`,
        { headers: { accept: 'application/json' } },
      );
      if (!res.ok) {
        activeRoundEl.innerHTML = `<span class="error">lookup failed (HTTP ${res.status})</span>`;
        return;
      }
      const round = (await res.json()) as { id: string; state: string } | null;
      if (round) {
        // A round.opened landed while this was in flight: the socket is ahead of
        // this REST read, so leave its round in place.
        if (gen !== roundGen) return;
        activeRoundId = round.id;
        activeRoundEl.innerHTML = `<code>${round.id}</code> <span class="pill ${
          round.state === 'betting_open' ? 'live' : ''
        }">${round.state}</span>`;
        await refreshTotals(opts);
      }
    } catch (err) {
      activeRoundEl.innerHTML = `<span class="error">${escapeHtml(describeFetchError(err))}</span>`;
    }
  }

  function pushEvent(e: RoundEvent): void {
    if (paused) return;
    events.unshift(e);
    if (events.length > MAX_LOG_ROWS) events.pop();
    renderLog();
  }

  function renderLog(): void {
    const visible = typeFilter
      ? events.filter((e) => e.type.toLowerCase().includes(typeFilter))
      : events;
    if (visible.length === 0) {
      logEl.innerHTML = `<p class="muted">${typeFilter ? `No events matching "${escapeHtml(typeFilter)}".` : 'Waiting for events…'}</p>`;
      return;
    }
    logEl.innerHTML = `<div style="max-height:50vh;overflow:auto"><table>
      <thead><tr><th>Time</th><th>Type</th><th>Round</th><th>Payload</th></tr></thead>
      <tbody>${visible
        .map(
          (e) => `<tr>
            <td>${fmtTime(e.ts)}</td>
            <td><code>${escapeHtml(e.type)}</code></td>
            <td><code>${escapeHtml(e.roundId.slice(-6))}</code></td>
            <td>${jsonTree(e.payload)}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table></div>`;
  }

  // Mint inspector token, open socket.io connection, subscribe to room
  try {
    const { token } = await deps.client.inspectorToken(deps.integratorId);
    socket = io(deps.gatewayUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
    });
    let everConnected = false;
    socket.on('connect', () => {
      setStatus('connected', 'live');
      socket!.emit('round.subscribe', { gameId: deps.gameId, integratorId: deps.integratorId });
      // A reconnect gap swallows every frame that fired while we were away, so
      // activeRoundId can be stale on the far side of it. Skipped on the first
      // connect — refreshActive() at the bottom already covers boot.
      if (everConnected) void refreshActive({ background: true });
      everConnected = true;
    });
    socket.on('disconnect', () => setStatus('disconnected', 'warn'));
    socket.on('connect_error', (err) => setStatus(`error: ${err.message}`, 'warn'));
    socket.on('round.event', (event: RoundEvent) => {
      // The gateway joins this socket to the base room of EVERY game enabled for
      // the integrator (sockets.ts loadEnabledGames), not just the one we
      // subscribed to. Unfiltered, another game's round.opened hijacked
      // activeRoundId and blanked the panel.
      if (event.gameId !== deps.gameId) return;
      pushEvent(event);
      if (event.type === 'round.opened') {
        activeRoundId = event.roundId;
        roundGen += 1;
        activeRoundEl.innerHTML = `<code>${event.roundId}</code> <span class="pill live">betting_open</span>`;
        // A freshly opened round has no bets, so render that instead of fetching
        // a known-empty result. The ++ retires any in-flight fetch for the
        // previous round.
        totalsSeq++;
        heatmapEl.innerHTML = '<p class="muted">No bets yet for this round.</p>';
      } else if (event.type === 'bet.placed') {
        void refreshTotals({ background: true });
      } else if (event.type === 'round.settled') {
        void refreshTotals({ background: true });
      }
    });
  } catch (err) {
    setStatus(`token error: ${err instanceof Error ? err.message : String(err)}`, 'warn');
  }

  const pauseBtn = root.querySelector<HTMLButtonElement>('#evt-pause');
  pauseBtn?.addEventListener('click', () => {
    paused = !paused;
    pauseBtn.textContent = paused ? 'Resume' : 'Pause';
    pauseBtn.classList.toggle('primary', paused);
  });
  root.querySelector<HTMLButtonElement>('#evt-clear')?.addEventListener('click', () => {
    events.length = 0;
    renderLog();
  });
  root.querySelector<HTMLInputElement>('#evt-filter')?.addEventListener('input', (ev) => {
    typeFilter = (ev.target as HTMLInputElement).value.trim().toLowerCase();
    renderLog();
  });

  // Initial state. The WS feed is the fast path from here; the poll below is
  // the backstop.
  await refreshActive();

  // Convergence poll. The WS feed is the fast path but not a guarantee —
  // singaporelottery suppresses bet.placed outright (module.privateBets), so it
  // emits no per-bet frame at all. Gated on tab visibility, not round state:
  // visibility cannot go stale, whereas gating on the same feed this backstops
  // would reintroduce the failure it covers. Restores the poll d4e383d7 dropped,
  // keeping that commit's actual remedy — the visibility guard, as in
  // system-health.ts.
  const totalsPoll = setInterval(() => {
    if (document.visibilityState !== 'visible' || totalsInFlight) return;
    void refreshTotals({ background: true });
  }, TOTALS_POLL_MS);
  // Coming back to the tab shouldn't cost a full interval of staleness.
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') void refreshTotals({ background: true });
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  // Cleanup on route change. Watch document.body with subtree: the router swaps
  // els.content.innerHTML wholesale, removing `root` as a unit — a mutation of
  // its PARENT, invisible to an observer scoped to root's own childList.
  const cleanupObserver = new MutationObserver(() => {
    if (!document.body.contains(root)) {
      clearInterval(totalsPoll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      socket?.disconnect();
      cleanupObserver.disconnect();
    }
  });
  cleanupObserver.observe(document.body, { childList: true, subtree: true });
}

/** Turn a fetch/parse failure into something an operator can act on. A JSON
 *  SyntaxError against the round-engine base means the request never reached
 *  round-engine — the edge served the admin SPA shell instead, i.e. the
 *  /round-engine/* reverse-proxy route is missing on this deployment. */
function describeFetchError(err: unknown): string {
  if (err instanceof SyntaxError) {
    return 'round-engine returned HTML, not JSON — the /round-engine/* proxy route is missing on this host';
  }
  return err instanceof Error ? err.message : String(err);
}

function jsonTree(value: unknown, depth = 0, parentKey?: string): string {
  if (value === null) return '<span class="muted">null</span>';
  if (typeof value === 'string') return `<span style="color:var(--success)">"${escapeHtml(value)}"</span>`;
  if (typeof value === 'number') {
    if (isTimestampKey(parentKey) && value > 1e12) {
      return `<span style="color:var(--accent)" title="${value}">${escapeHtml(fmtDateTime(value))}</span>`;
    }
    return `<span style="color:var(--accent)">${value}</span>`;
  }
  if (typeof value === 'boolean') return `<span style="color:var(--accent-2)">${value}</span>`;
  if (Array.isArray(value)) {
    if (value.length === 0) return '<span class="muted">[]</span>';
    const summary = `Array(${value.length})`;
    const inner = value.map((v) => `<li style="margin:2px 0">${jsonTree(v, depth + 1)}</li>`).join('');
    return `<details ${depth < 1 ? 'open' : ''} style="display:inline-block;vertical-align:top"><summary style="cursor:pointer;color:var(--text-dim);font-size:11px">${summary}</summary><ul style="margin:4px 0 4px 12px;padding:0;list-style:none">${inner}</ul></details>`;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    if (keys.length === 0) return '<span class="muted">{}</span>';
    const summary = `Object(${keys.length} keys)`;
    const inner = keys
      .map((k) => `<li style="margin:2px 0"><span style="color:var(--text-dim)">${escapeHtml(k)}:</span> ${jsonTree(obj[k], depth + 1, k)}</li>`)
      .join('');
    return `<details ${depth < 1 ? 'open' : ''} style="display:inline-block;vertical-align:top"><summary style="cursor:pointer;color:var(--text-dim);font-size:11px">${summary}</summary><ul style="margin:4px 0 4px 12px;padding:0;list-style:none">${inner}</ul></details>`;
  }
  return `<code>${escapeHtml(String(value))}</code>`;
}

// Treat any *At-suffixed key (startedAt, endedAt, bettingClosesAt, settledAt, …)
// as an epoch-ms timestamp for display formatting.
function isTimestampKey(key: string | undefined): boolean {
  return Boolean(key && /At$/.test(key));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
